/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps } from 'preact';
import { useCallback, useMemo } from 'preact/hooks';
import { cssProps } from '@oracle/oraclejet-cssprops';
import {
  CurrentKeyDetail,
  DataState,
  ItemActionDetail,
  ListItemContext,
  Metadata,
  SelectionDetail
} from '../UNSAFE_Collection';
import { HighlightText } from '../UNSAFE_HighlightText';
import { List, SkeletonContainer } from '../PRIVATE_List';
import { Flex } from '../UNSAFE_Flex';
import { Skeleton } from '../UNSAFE_Skeleton';
import { Keys } from '../utils/UNSAFE_keys';
import { ItemTextType } from '../utils/UNSAFE_selectUtils';
import { renderItemText } from './itemTextUtils';

const itemStyles = cssProps`${{
  paddingBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-3x)',
  paddingLeft: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-3x)',
  paddingRight: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-3x)',
  paddingTop: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-3x)'
}}`;

type PickedPropsFromList = Pick<
  ComponentProps<typeof List>,
  'aria-label' | 'currentItemVariant' | 'selectionMode'
>;

// pick the props from the ListItemContext instead of extending it directly so that we
// don't automatically expose any new props that may be added to it
type PickedPropsFromListItemContext<K, D> = Pick<
  ListItemContext<K, D>,
  'data' | 'index' | 'selector'
>;
type ItemRendererSelectionDetail<K> = {
  value: Set<K>;
  target: EventTarget | null;
};
type ItemRendererMetadata<K> = Pick<Metadata<K>, 'key' | 'suggestion'>;
type ItemRendererProps<K, D> = PickedPropsFromListItemContext<K, D> & {
  metadata: ItemRendererMetadata<K>;
  searchText?: string;
  selectedKeys?: Set<K>;
  onSelectionChange?: (detail: ItemRendererSelectionDetail<K>) => void;
};

type Props<K extends string | number, D extends Record<string, any>> = PickedPropsFromList & {
  itemRenderer?: (itemRendererProps: ItemRendererProps<K, D>) => ComponentChildren;
  itemText: ItemTextType<K, D>;
  // props from List that we constrain or unconstrain
  currentKey?: K;
  data?: DataState<K, D> | null;
  onCurrentKeyChange?: (detail: CurrentKeyDetail<K>) => void;
  onItemAction?: (detail: ItemActionDetail<K, D>) => void;
  onLoadRange?: ComponentProps<typeof List>['onLoadRange'];
  onSelectionChange?: (detail: SelectionDetail<K>) => void;
  searchText?: string;
  selectedKeys?: Set<K>;
};

const noOp = () => {};

export function DefaultList<K extends string | number, D extends Record<string, any>>({
  'aria-label': ariaLabel,
  currentItemVariant,
  currentKey,
  data,
  itemRenderer,
  itemText,
  onCurrentKeyChange,
  onItemAction,
  onLoadRange = noOp,
  onSelectionChange,
  searchText,
  selectedKeys,
  selectionMode
}: Props<K, D>) {
  const listItemRenderer = useCallback(
    (listItemContext: ListItemContext<K, D>) => {
      // specify the listItemContext props that we want to expose individually instead of
      // spreading the whole object so that we don't automatically include any new props that
      // may be added to it
      const itemRendererProps: ItemRendererProps<K, D> = {
        data: listItemContext.data,
        index: listItemContext.index,
        metadata: listItemContext.metadata,
        selector: listItemContext.selector,
        searchText,
        selectedKeys,
        onSelectionChange: ({ target, value }) => {
          onSelectionChange?.({
            target,
            value: { all: false, keys: value }
          });
        }
      };
      const itemContext = {
        data: listItemContext.data,
        key: listItemContext.metadata.key,
        metadata: listItemContext.metadata
      };
      return itemRenderer ? (
        itemRenderer(itemRendererProps)
      ) : (
        <Flex>
          {listItemContext.selector && listItemContext.selector()}
          <div class={itemStyles}>
            <HighlightText matchText={searchText}>
              {renderItemText(itemContext, itemText) as string}
            </HighlightText>
          </div>
        </Flex>
      );
    },
    [itemRenderer, itemText, onSelectionChange, searchText, selectedKeys]
  );

  const keys = useMemo(() => {
    return { all: false, keys: selectedKeys ?? new Set<K>() };
  }, [selectedKeys]) as Keys<K>;

  return (
    <List<K, D>
      aria-label={ariaLabel}
      allowTabbableMode={false}
      currentItemVariant={currentItemVariant}
      currentKey={currentKey}
      data={data ?? null}
      loadingIndicator={loadingIndicator}
      onCurrentKeyChange={onCurrentKeyChange}
      onItemAction={onItemAction}
      onLoadRange={onLoadRange}
      onSelectionChange={onSelectionChange}
      role="listbox"
      selectedKeys={keys}
      selectionMode={selectionMode}>
      {(context: ListItemContext<K, D>) => listItemRenderer(context)}
    </List>
  );
}

/**
 * Custom 3 Skeleton loader for select multiple
 * TODO: to remove this once  is solved
 */
const loadingIndicator = (
  <SkeletonContainer minimumCount={3}>
    {() => {
      return (
        <Flex height="12x" align="center">
          <Skeleton height="4x" />
        </Flex>
      );
    }}
  </SkeletonContainer>
);
